import { defineStore } from "pinia";
import { CategorySplit, DropLocation, DropPool } from "@/types/dropPool";
import { isInRadius } from "@/utils/geolocationTools";
import { isInDateRange } from "@/utils/dateTools";
import { APIClient } from "@/utils/httpClient";
import { Category } from "@/types/category";
import { PoolSegment } from "@/types/poolSegment";
import { poolSegmentStore } from "./poolSegmentStore";
import { categoryStore } from "./categoryStore";
import { authStore } from "./authStore";
import { prizeStore } from "./prizeStore";
import { DropZone, ZoneCheckResponse } from "@/types/dropZone";

interface IDropPoolStoreState {
	pools: DropPool[];
	userLocation: [number, number];
	locationAccuracy: number;
	updatingLocation: boolean;
	geolocationPermissionStatus: string;
	cameraPermissionStatus: string;
	permissionGranted: boolean;
	activePool: DropPool | null;
	activeDropLocation: DropLocation | null;
	loading: boolean;
	watcher: number;
	dropZonesByPool: { [poolID: string]: DropZone[] };
	zoneAllowed: boolean;
	allowedPrizeIDs: string[];
	lastZoneCheckLocation: { lat: number; lng: number } | null;
	lastZoneCheckResult: ZoneCheckResponse | null;
}

export const dropPoolStore = defineStore("dropPoolStore", {
	persist: false,
	state: () =>
	({
		pools: [],
		lastUserLocation: [0, 0],
		userLocation: [0, 0],
		locationAccuracy: 100000,
		distanceTraveled: 0,
		updatingLocation: false,
		geolocationPermissionStatus: "denied",
		cameraPermissionStatus: "denied",
		permissionGranted: false,
		activePool: null,
		activeDropLocation: null,
		loading: false,
		watcher: -1,
		dropZonesByPool: {},
		zoneAllowed: false,
		allowedPrizeIDs: [],
		lastZoneCheckLocation: null,
		lastZoneCheckResult: null,
	} as IDropPoolStoreState),
	getters: {
		getPools(state: IDropPoolStoreState) {
			return state.pools;
		},
		getLoading(state: IDropPoolStoreState) {
			return state.loading;
		},
		getPoolByID: (state: IDropPoolStoreState) => (id: string) => {
			return state.pools.find((e: DropPool) => e.id == id);
		},
		getActivePool(state: IDropPoolStoreState) {
			return state.activePool;
		},
		getActiveDropLocation(state: IDropPoolStoreState) {
			return state.activeDropLocation;
		},
		getGeolocationPermissionStatus(state: IDropPoolStoreState) {
			return state.geolocationPermissionStatus;
		},
		getPermissionGranted(state: IDropPoolStoreState): boolean {
			return state.permissionGranted;
		},
		getUserLocation(state: IDropPoolStoreState) {
			return state.userLocation;
		},
		getLocationAccuracy(state: IDropPoolStoreState) {
			return state.locationAccuracy;
		},
		getWatcher(state: IDropPoolStoreState) {
			return state.watcher;
		},
		getDummyVatLocations(state: IDropPoolStoreState) {
			const pools = state.pools.filter((e: DropPool) => {
				return (
					isInDateRange(
						new Date().toISOString(),
						`${e.startDate}T00:00:00.000Z`,
						`${e.endDate}T23:59:59.999Z`
					) &&
					e.dropType == "gps" &&
					e.enabled == true
				);
			});
			const segments = poolSegmentStore().segments;
			const dummyLocations: DropLocation[] = [];
			for (let i = 0; i < pools.length; i++) {
				const segment = segments.find(
					(e: PoolSegment) => e.poolID == pools[i].id
				);
				if (segment) {
					const categories = segment.items.map(
						(e: CategorySplit) => e.categoryID
					);
					for (let j = 0; j < pools[i].dropLocations.length; j++) {
						const loc = pools[i].dropLocations[j];
						loc.radius = pools[i].dropLocationRadius;
						const cat: Category | undefined = categoryStore().getItemByID(
							categories[Math.floor(Math.random() * categories.length)]
						);
						if (cat && cat.image) {
							loc.marker = cat.image;
							dummyLocations.push(loc);
						}
					}
				}
			}
			return dummyLocations;
		},
		getDropZonesByPool: (state: IDropPoolStoreState) => (poolID: string) => {
			return state.dropZonesByPool[poolID] || [];
		},
		getZoneAllowed(state: IDropPoolStoreState) {
			return state.zoneAllowed;
		},
		getAllowedPrizeIDs(state: IDropPoolStoreState) {
			return state.allowedPrizeIDs;
		},
	},
	actions: {
		getAvailablePools() {
			return this.pools.filter((e: DropPool) => {
				return isInDateRange(
					new Date().toISOString(),
					`${e.startDate}T${e.dropIntervalStart}:00.000Z`,
					`${e.endDate}T${e.dropIntervalEnd}:00.000Z`
				);
			});
		},
		setActivePool(pool: DropPool | null) {
			this.activePool = pool;
			if (!pool) {
				this.activeDropLocation = null;
				return false;
			}

			if (pool.dropType === "gps" && pool.dropLocations && pool.dropLocations.length > 0) {
				const location = pool.dropLocations.find((f: DropLocation) =>
					isInRadius(
						[this.userLocation[1], this.userLocation[0]],
						[f.latitude, f.longitude],
						pool.dropLocationRadius
					)
				);
				this.activeDropLocation = location || null;
			} else {
				this.activeDropLocation = null;
			}
			return true;
		},
		saveItems(pools: DropPool[]) {
			const enabledPools = pools.filter((e) => e.enabled == true);
			this.pools = enabledPools;
		},
		setLoading(loading: boolean) {
			this.loading = loading;
		},
		clearItems() {
			this.pools = [];
		},
		saveWatcher(watcher: number) {
			this.watcher = watcher;
		},
		saveUserLocation(location: [number, number]) {
			this.userLocation = location;
			// store.commit("dropPoolStore/saveActiveDropPool");
		},
		saveLocationAccuracy(locationAccuracy: number) {
			this.locationAccuracy = locationAccuracy;
		},
		saveDropZones(params: { poolID: string; zones: DropZone[] }) {
			this.dropZonesByPool = { ...this.dropZonesByPool, [params.poolID]: params.zones };
		},
		setZoneAllowed(params: { allowed: boolean; allowedPrizeIDs: string[] }) {
			this.zoneAllowed = params.allowed;
			this.allowedPrizeIDs = params.allowedPrizeIDs;
		},
		saveActiveDropPool(options?: {
			poolID?: string | null;
			dropType?: string | null;
			fallbackDropType?: string | null;
		}) {
			const pools = this.getAvailablePools();
			const activePoolID = options?.poolID ?? null;
			if (activePoolID) {
				const pool = pools.find((e: DropPool) => e.id === activePoolID) ?? null;
				if (pool) {
					this.setActivePool(pool);
					return;
				}
			}

			const preferredDropType = options?.dropType ?? this.activePool?.dropType ?? "pin";
			if (preferredDropType) {
				const pool = pools.find((e: DropPool) => e.dropType === preferredDropType) ?? null;
				if (pool) {
					this.setActivePool(pool);
					return;
				}
			}

			const fallbackDropType = options?.fallbackDropType ?? "gps";
			if (fallbackDropType) {
				const fallbackPool =
					pools.find((e: DropPool) => e.dropType === fallbackDropType) ?? null;
				if (fallbackPool) {
					this.setActivePool(fallbackPool);
					return;
				}
			}

			this.setActivePool(null);
			console.log("activePool", this.activePool);
		},
		saveActivePinPool(): boolean {
			const previousPoolID = this.activePool?.id ?? null;
			this.saveActiveDropPool({ dropType: "pin" });
			return this.activePool?.dropType === "pin" && this.activePool?.id !== previousPoolID;
		},
		resetActiveDropPool() {
			this.activePool = null;
			this.activeDropLocation = null;
		},
		async getDropPools() {
			const campaignID = authStore().campaignID;
			const endpoint = `/campaigns/${campaignID}/dropPools`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					this.saveItems(response.data);
				}
			} catch (error: any) {
				console.error(error);
			}
		},
		async getMapData() {
			const campaignID = authStore().campaignID;
			const endpoint = `/campaigns/${campaignID}/mapdata`;
			try {
				const response = await APIClient.get(endpoint);

				if (response.success) {
					this.saveItems(response.data.pools);
					categoryStore().addItems(response.data.categories);
					prizeStore().saveItems(response.data.prizes);
					poolSegmentStore().setItems(response.data.segments);
				}
			} catch (error: any) {
				console.error(error);
			}
		},
		async fetchDropPoolZones(poolID: string) {
			const campaignID = authStore().campaignID;
			if (!campaignID) return [] as DropZone[];
			const endpoint = `/campaigns/${campaignID}/dropPools/${poolID}`;
			try {
				const response = await APIClient.get(endpoint);
				if ((response as any).success) {
					const zones = ((response as any).data?.DropPool?.dropZones || []) as DropZone[];
					this.saveDropZones({ poolID, zones });
					return zones;
				}
			} catch (error: unknown) {
				console.error(error);
			}
			return [] as DropZone[];
		},
		async zoneCheck(params: { poolID: string; latitude: number; longitude: number; force?: boolean }) {
			const campaignID = authStore().campaignID;
			if (!campaignID) return null as ZoneCheckResponse | null;
			const { poolID, latitude, longitude } = params;
			const threshold = 0.0001; // ~11m
			if (!params.force && this.lastZoneCheckLocation) {
				const dLat = latitude - this.lastZoneCheckLocation.lat;
				const dLng = longitude - this.lastZoneCheckLocation.lng;
				if (Math.hypot(dLat, dLng) < threshold && this.lastZoneCheckResult) {
					return this.lastZoneCheckResult;
				}
			}
			try {
				const endpoint = `/campaigns/${campaignID}/dropPools/${poolID}/zone-check`;
				const response = await APIClient.post(endpoint, { latitude, longitude });
				if ((response as any).success) {
					const rc = (response as any) as ZoneCheckResponse;
					this.setZoneAllowed({ allowed: rc.data.allowed, allowedPrizeIDs: rc.data.allowedPrizeIDs });
					this.lastZoneCheckLocation = { lat: latitude, lng: longitude };
					this.lastZoneCheckResult = rc;
					return rc;
				}
			} catch (error: unknown) {
				console.error(error);
			}
			return null;
		},
		async renderARTemplate(params: {
			arTemplateID: string;
			walletID: string;
			dropPoolID: string;
			userLocation?: [number, number];
			deepLinkID: string;
		}) {
			const campaignID = authStore().campaignID;
			const endpoint = `/campaigns/${campaignID}/artemplates/${params.arTemplateID}/render`;
			try {
				const response = await APIClient.post(endpoint, {
					walletID: params.walletID,
					dropPoolID: params.dropPoolID,
					userLocation: params.userLocation,
					deepLinkID: params.deepLinkID,
				});
				if (response.success) {
					return response.data;
				}
			} catch (error: any) {
				console.error(error);
			}
		},
		async getARTemplate(arTemplateID: string) {
			const campaignID = authStore().campaignID;
			const endpoint = `/campaigns/${campaignID}/artemplates/${arTemplateID}/active`;
			try {
				const response = await APIClient.get(endpoint);
				if (response.success) {
					return response.data;
				}
			} catch (error: any) {
				console.error(error);
			}
		},
	},
});
